/**
 * Pricing Configuration Editor
 * Loads merged config (file defaults + Redis overrides) from /api/admin/pricing
 * Saves full config back via PUT.
 */

import { useState, useEffect } from 'react';
import { pricing as pricingApi } from '../utils/adminApi';
import bundledConfig from '../../config/pricing_config.json';

function PricingEditor() {
  const [config, setConfig] = useState(bundledConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    pricingApi.get()
      .then(remote => {
        setConfig(prev => ({ ...prev, ...remote }));
      })
      .catch(err => {
        console.error('Failed to load pricing:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const updateConfig = (path, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] === undefined) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
    setHasChanges(true);
    setSaveMessage(null);
  };

  const updateAddOn = (index, field, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!Array.isArray(next.add_ons)) next.add_ons = [];
      next.add_ons[index] = { ...next.add_ons[index], [field]: value };
      return next;
    });
    setHasChanges(true);
    setSaveMessage(null);
  };

  const addAddOn = () => {
    setConfig(prev => ({
      ...prev,
      add_ons: [...(prev.add_ons || []), { id: `addon-${Date.now()}`, name: 'New Add-on', duration: '5 min', price: 0 }]
    }));
    setHasChanges(true);
  };

  const removeAddOn = (index) => {
    setConfig(prev => ({
      ...prev,
      add_ons: (prev.add_ons || []).filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const updateService = (index, field, value) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!Array.isArray(next.display_services)) next.display_services = [];
      next.display_services[index] = { ...next.display_services[index], [field]: value };
      return next;
    });
    setHasChanges(true);
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await pricingApi.update(config);
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Pricing saved. Customers will see updated prices within ~1 minute.' });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pricing-editor"><p>Loading pricing configuration…</p></div>;
  }

  return (
    <div className="pricing-editor">
      <div className="admin-page-header">
        <h1>Pricing Configuration</h1>
        <p>Edit displayed service prices, add-ons, and internal pricing rules</p>
      </div>

      {(hasChanges || saveMessage) && (
        <div className={`unsaved-banner ${saveMessage?.type === 'error' ? 'error' : ''} ${saveMessage?.type === 'success' ? 'success' : ''}`}>
          <span>
            {saveMessage ? saveMessage.text : 'You have unsaved changes'}
          </span>
          {hasChanges && (
            <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      )}

      {/* Display Services — what customers see on home/book pages */}
      <div className="admin-card">
        <h2>Service Cards (Home + Book pages)</h2>
        <p className="card-subtitle">These prices appear on the website's service cards.</p>
        <div className="services-list">
          {(config.display_services || []).map((s, i) => (
            <div key={s.id ?? i} className="service-row">
              <div className="service-row-main">
                <input
                  type="text"
                  className="admin-input"
                  value={s.title || ''}
                  onChange={(e) => updateService(i, 'title', e.target.value)}
                  placeholder="Title"
                />
                <div className="input-with-prefix small">
                  <span>$</span>
                  <input
                    type="number"
                    className="admin-input"
                    value={s.price ?? ''}
                    onChange={(e) => updateService(i, 'price', parseFloat(e.target.value) || 0)}
                    placeholder="Price"
                  />
                </div>
                <input
                  type="text"
                  className="admin-input small"
                  value={s.duration || ''}
                  onChange={(e) => updateService(i, 'duration', e.target.value)}
                  placeholder="Duration"
                />
              </div>
              <textarea
                className="admin-input"
                value={s.description || ''}
                onChange={(e) => updateService(i, 'description', e.target.value)}
                placeholder="Description"
                rows={2}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Add-ons */}
      <div className="admin-card">
        <h2>Add-ons (in booking flow)</h2>
        <p className="card-subtitle">Optional services customers can add during booking (filters, tire rotation, etc.)</p>
        <div className="addons-list">
          {(config.add_ons || []).map((a, i) => (
            <div key={a.id ?? i} className="addon-row">
              <input
                type="text"
                className="admin-input"
                value={a.name || ''}
                onChange={(e) => updateAddOn(i, 'name', e.target.value)}
                placeholder="Name"
              />
              <input
                type="text"
                className="admin-input small"
                value={a.duration || ''}
                onChange={(e) => updateAddOn(i, 'duration', e.target.value)}
                placeholder="Duration"
              />
              <div className="input-with-prefix small">
                <span>$</span>
                <input
                  type="number"
                  className="admin-input"
                  value={a.price ?? ''}
                  onChange={(e) => updateAddOn(i, 'price', parseFloat(e.target.value) || 0)}
                />
              </div>
              <button className="admin-btn admin-btn-danger" onClick={() => removeAddOn(i)}>Remove</button>
            </div>
          ))}
        </div>
        <button className="admin-btn admin-btn-secondary" onClick={addAddOn}>+ Add Add-on</button>
      </div>

      {/* Package Base Prices (internal pricing engine) */}
      <div className="admin-card">
        <h2>Package Base Prices (pricing engine)</h2>
        <p className="card-subtitle">Used internally by the booking calculator for vehicle-aware pricing.</p>
        <div className="price-grid">
          {Object.entries(config.package_base_prices || {}).map(([key, value]) => (
            <div key={key} className="admin-form-group">
              <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Package</label>
              <div className="input-with-prefix">
                <span>$</span>
                <input
                  type="number"
                  className="admin-input"
                  value={value}
                  onChange={(e) => updateConfig(`package_base_prices.${key}`, parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Labor Prices */}
      <div className="admin-card">
        <h2>Labor Prices</h2>
        <div className="price-grid">
          {Object.entries(config.labor_prices || {}).map(([key, value]) => (
            <div key={key} className="admin-form-group">
              <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
              <div className="input-with-prefix">
                <span>$</span>
                <input
                  type="number"
                  className="admin-input"
                  value={value}
                  onChange={(e) => updateConfig(`labor_prices.${key}`, parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fees */}
      <div className="admin-card">
        <h2>Fees</h2>
        <div className="price-grid">
          {Object.entries(config.fees || {}).map(([key, value]) => (
            <div key={key} className="admin-form-group">
              <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
              <div className="input-with-prefix">
                <span>$</span>
                <input
                  type="number"
                  step="0.01"
                  className="admin-input"
                  value={value}
                  onChange={(e) => updateConfig(`fees.${key}`, parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .unsaved-banner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          color: #92400e;
          font-weight: 500;
          gap: 1rem;
        }
        .unsaved-banner.success {
          background: #dcfce7;
          border-color: #16a34a;
          color: #166534;
        }
        .unsaved-banner.error {
          background: #fee2e2;
          border-color: #dc2626;
          color: #991b1b;
        }
        .card-subtitle {
          color: #6b7280;
          font-size: 0.9rem;
          margin: -0.5rem 0 1rem 0;
        }
        .price-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }
        .services-list,
        .addons-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .service-row {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        .service-row-main {
          display: grid;
          grid-template-columns: 2fr 110px 110px;
          gap: 0.5rem;
        }
        .addon-row {
          display: grid;
          grid-template-columns: 2fr 110px 110px auto;
          gap: 0.5rem;
          align-items: center;
        }
        .input-with-prefix,
        .input-with-suffix {
          display: flex;
          align-items: center;
        }
        .input-with-prefix span {
          padding: 0.625rem 0.75rem;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-right: none;
          border-radius: 8px 0 0 8px;
          color: #6b7280;
        }
        .input-with-prefix.small {
          width: 100%;
        }
        .input-with-prefix .admin-input {
          border-radius: 0 8px 8px 0;
          width: 100%;
        }
        .admin-input.small {
          width: 100%;
        }
        .admin-btn-danger {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }
        .admin-btn-danger:hover {
          background: #fecaca;
        }
      `}</style>
    </div>
  );
}

export default PricingEditor;
